import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner, Row, Col, Nav, Tab } from 'react-bootstrap';
import { FiDownload, FiFileText, FiCalendar, FiExternalLink, FiCopy, FiArrowLeft } from 'react-icons/fi';
import { 
    generateBulkTransfersCsvRequest, 
    generateBulkTransfersCsvByPolicyCodesRequest,
    generateBulkTransfersCsvEndOfStayRequest,
    generateBulkTransfersCsvEndOfStayByPolicyCodesRequest
} from '../../store/actions/dataActions';
import { downloadS3File } from '../../services/api';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsBulkTransfers.css';

// Fonction helper pour télécharger un fichier depuis une URL ou un chemin S3
const downloadFile = async (urlOrPath, filename) => {
    try {
        // Si c'est une URL HTTP/HTTPS, on télécharge directement
        if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
            // Pour les URLs S3 pré-signées, on peut avoir des problèmes CORS
            // On essaie d'abord avec fetch, sinon on ouvre dans un nouvel onglet
            try {
                const response = await fetch(urlOrPath, {
                    method: 'GET',
                    mode: 'cors',
                    credentials: 'omit',
                });
                
                if (!response.ok) {
                    throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
                }
                
                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            } catch (fetchError) {
                console.warn('Erreur fetch, tentative avec ouverture directe:', fetchError);
                // Si fetch échoue (CORS, etc.), on ouvre directement dans un nouvel onglet
                // et on force le téléchargement via un lien temporaire
                const link = document.createElement('a');
                link.href = urlOrPath;
                link.download = filename;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } else if (urlOrPath.startsWith('s3://')) {
            // Si c'est un chemin S3, on utilise l'API backend
            try {
                const response = await downloadS3File(urlOrPath);
                const blob = new Blob([response.data], { 
                    type: response.headers['content-type'] || 'application/octet-stream' 
                });
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            } catch (apiError) {
                console.error('Erreur API lors du téléchargement:', apiError);
                // Si l'endpoint n'existe pas encore, on affiche un message
                alert('Le téléchargement via l\'API n\'est pas encore disponible. Le fichier est disponible dans S3 : ' + urlOrPath);
            }
        } else {
            throw new Error('Format d\'URL non supporté');
        }
    } catch (error) {
        console.error('Erreur lors du téléchargement:', error);
        alert('Erreur lors du téléchargement du fichier : ' + error.message);
    }
};

// Fonction helper pour copier du texte dans le presse-papiers
const copyToClipboard = async (text, setCopied) => {
    try {
        await navigator.clipboard.writeText(text);
        if (setCopied) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    } catch (err) {
        console.error('Erreur lors de la copie:', err);
        // Fallback pour les navigateurs plus anciens
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            if (setCopied) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        } catch (fallbackErr) {
            console.error('Erreur fallback:', fallbackErr);
        }
        document.body.removeChild(textArea);
    }
};

// Composant pour afficher les URLs S3 et les boutons de téléchargement
const FileDownloadSection = ({ data }) => {
    const [copiedCsvUrl, setCopiedCsvUrl] = useState(false);
    const [copiedCsvS3Path, setCopiedCsvS3Path] = useState(false);
    const [copiedExcelUrl, setCopiedExcelUrl] = useState(false);
    const [copiedExcelS3Path, setCopiedExcelS3Path] = useState(false);
    
    if (!data) return null;

    // Nouvelle structure : fichiers dans un tableau files
    let csvFile = null;
    let excelFile = null;

    if (data.files && Array.isArray(data.files)) {
        csvFile = data.files.find(file => file.type === 'CSV');
        excelFile = data.files.find(file => file.type === 'Excel');
    }

    // Ancienne structure : URLs directes (pour compatibilité)
    const csvUrl = data.csv_url || data.csv_s3_url || data.transfers_csv_url || data.transfers_file_url;
    const excelUrl = data.excel_url || data.excel_s3_url || data.paid_daily_excel_url || data.paid_daily_file_url;
    
    // Extraire les S3 paths depuis l'ancienne structure si disponibles
    const csvS3Path = data.csv_s3_path || data.transfers_csv_s3_path || data.transfers_s3_path || 
                       (csvFile && csvFile.s3_path) || (csvFile && csvFile.s3_file_path);
    const excelS3Path = data.excel_s3_path || data.paid_daily_excel_s3_path || data.paid_daily_s3_path ||
                        (excelFile && excelFile.s3_path) || (excelFile && excelFile.s3_file_path);

    // Utiliser la nouvelle structure si disponible, sinon l'ancienne
    const finalCsvFile = csvFile ? {
        ...csvFile,
        file_path: csvFile.file_path || csvFile.url || csvUrl,
        s3_path: csvFile.s3_path || csvFile.s3_file_path || csvS3Path || null
    } : (csvUrl ? { 
        file_path: csvUrl, 
        filename: csvUrl.split('/').pop() || 'transfers.csv',
        s3_path: csvS3Path || null
    } : null);
    
    const finalExcelFile = excelFile ? {
        ...excelFile,
        file_path: excelFile.file_path || excelFile.url || excelUrl,
        s3_path: excelFile.s3_path || excelFile.s3_file_path || excelS3Path || null
    } : (excelUrl ? { 
        file_path: excelUrl, 
        filename: excelUrl.split('/').pop() || 'paid_daily.xlsx',
        s3_path: excelS3Path || null
    } : null);

    if (!finalCsvFile && !finalExcelFile) {
        return null;
    }

    // Extraire l'URL de téléchargement (priorité: file_path HTTPS > s3_path)
    const getDownloadUrl = (file) => {
        // Si file_path est une URL HTTPS, l'utiliser directement
        if (file.file_path && (file.file_path.startsWith('http://') || file.file_path.startsWith('https://'))) {
            return file.file_path;
        }
        // Sinon, utiliser s3_path si disponible
        if (file.s3_path) {
            return file.s3_path;
        }
        // En dernier recours, utiliser file_path
        return file.file_path;
    };

    return (
        <div className="mt-3">
            <strong>Fichiers disponibles :</strong>
            <div className="d-flex flex-column gap-2 mt-2">
                {finalCsvFile && (
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => {
                                const downloadUrl = getDownloadUrl(finalCsvFile);
                                const filename = finalCsvFile.filename || downloadUrl.split('/').pop() || 'transfers.csv';
                                downloadFile(downloadUrl, filename);
                            }}
                        >
                            <FiDownload className="me-2" />
                            Fichier banque
                        </Button>
                        {finalCsvFile.total_records !== undefined && (
                            <span className="text-muted small">
                                ({finalCsvFile.total_records} enregistrement{finalCsvFile.total_records > 1 ? 's' : ''})
                            </span>
                        )}
                        {finalCsvFile.file_path && finalCsvFile.file_path.startsWith('http') && (
                            <a 
                                href={finalCsvFile.file_path} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-decoration-none ms-2"
                            >
                                <FiExternalLink className="me-1" />
                                Ouvrir
                            </a>
                        )}
                    </div>
                )}
                {finalExcelFile && (
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                                const downloadUrl = getDownloadUrl(finalExcelFile);
                                const filename = finalExcelFile.filename || downloadUrl.split('/').pop() || 'paid_daily.xlsx';
                                downloadFile(downloadUrl, filename);
                            }}
                        >
                            <FiDownload className="me-2" />
                            Fichier claim update status paid
                        </Button>
                        {finalExcelFile.total_records !== undefined && (
                            <span className="text-muted small">
                                ({finalExcelFile.total_records} enregistrement{finalExcelFile.total_records > 1 ? 's' : ''})
                            </span>
                        )}
                        {finalExcelFile.file_path && finalExcelFile.file_path.startsWith('http') && (
                            <a 
                                href={finalExcelFile.file_path} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-decoration-none ms-2"
                            >
                                <FiExternalLink className="me-1" />
                                Ouvrir
                            </a>
                        )}
                    </div>
                )}
            </div>
            {(finalCsvFile || finalExcelFile) && (
                <div className="mt-2">
                    <small className="text-muted">
                        <strong>Informations des fichiers :</strong>
                        {finalCsvFile && (
                            <div className="mt-2">
                                <strong>Fichier banque :</strong>
                                {finalCsvFile.file_path && (
                                    <div className="d-flex align-items-center gap-2 mt-1">
                                        <code className="small flex-grow-1" style={{ wordBreak: 'break-all' }}>{finalCsvFile.file_path}</code>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => copyToClipboard(finalCsvFile.file_path, setCopiedCsvUrl)}
                                            title="Copier l'URL"
                                            style={{ minWidth: '80px' }}
                                        >
                                            <FiCopy className="me-1" />
                                            {copiedCsvUrl ? 'Copié!' : 'Copier'}
                                        </Button>
                                    </div>
                                )}
                                {finalCsvFile.s3_path && (
                                    <div className="d-flex align-items-center gap-2 mt-1">
                                        <div className="flex-grow-1">
                                            <strong>S3 Path :</strong> <code className="small" style={{ wordBreak: 'break-all' }}>{finalCsvFile.s3_path}</code>
                                        </div>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => copyToClipboard(finalCsvFile.s3_path, setCopiedCsvS3Path)}
                                            title="Copier le chemin S3"
                                            style={{ minWidth: '80px' }}
                                        >
                                            <FiCopy className="me-1" />
                                            {copiedCsvS3Path ? 'Copié!' : 'Copier'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                        {finalExcelFile && (
                            <div className="mt-2">
                                <strong>Fichier claim update status paid :</strong>
                                {finalExcelFile.file_path && (
                                    <div className="d-flex align-items-center gap-2 mt-1">
                                        <code className="small flex-grow-1" style={{ wordBreak: 'break-all' }}>{finalExcelFile.file_path}</code>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => copyToClipboard(finalExcelFile.file_path, setCopiedExcelUrl)}
                                            title="Copier l'URL"
                                            style={{ minWidth: '80px' }}
                                        >
                                            <FiCopy className="me-1" />
                                            {copiedExcelUrl ? 'Copié!' : 'Copier'}
                                        </Button>
                                    </div>
                                )}
                                {finalExcelFile.s3_path && (
                                    <div className="d-flex align-items-center gap-2 mt-1">
                                        <div className="flex-grow-1">
                                            <strong>S3 Path :</strong> <code className="small" style={{ wordBreak: 'break-all' }}>{finalExcelFile.s3_path}</code>
                                        </div>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => copyToClipboard(finalExcelFile.s3_path, setCopiedExcelS3Path)}
                                            title="Copier le chemin S3"
                                            style={{ minWidth: '80px' }}
                                        >
                                            <FiCopy className="me-1" />
                                            {copiedExcelS3Path ? 'Copié!' : 'Copier'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </small>
                </div>
            )}
        </div>
    );
};

const AdminTripsBulkTransfers = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { 
        generateBulkTransfersCsv, 
        generateBulkTransfersCsvByPolicyCodes,
        generateBulkTransfersCsvEndOfStay,
        generateBulkTransfersCsvEndOfStayByPolicyCodes
    } = useSelector((state) => state.data);

    const [activeTab, setActiveTab] = useState('daily'); // 'daily' or 'endOfStay'
    const [method, setMethod] = useState('byDate'); // 'byDate', 'byPolicyCodes', 'endOfStay', 'endOfStayByPolicyCodes'
    const [claimDate, setClaimDate] = useState('');
    const [policyCodes, setPolicyCodes] = useState('');
    const [claimDateByCodes, setClaimDateByCodes] = useState('');
    const [tripEndDateFrom, setTripEndDateFrom] = useState('');
    const [tripEndDateTo, setTripEndDateTo] = useState('');
    const [policyCodesEndOfStay, setPolicyCodesEndOfStay] = useState('');
    const [tripEndDateFromByCodes, setTripEndDateFromByCodes] = useState('');
    const [tripEndDateToByCodes, setTripEndDateToByCodes] = useState('');

    // Obtenir la date du jour au format YYYY-MM-DD
    useEffect(() => {
        const today = new Date().toISOString().split('T')[0];
        setClaimDate(today);
        setClaimDateByCodes(today);
    }, []);

    const handleGenerateByDate = () => {
        const params = {};
        if (claimDate) {
            params.claim_date = claimDate;
        }
        dispatch(generateBulkTransfersCsvRequest(params));
    };

    const handleGenerateByPolicyCodes = () => {
        const codes = policyCodes
            .split(',')
            .map(code => code.trim())
            .filter(code => code.length > 0);

        if (codes.length === 0) {
            alert('Veuillez entrer au moins un code de police');
            return;
        }

        const params = {
            policy_codes: codes,
        };
        if (claimDateByCodes) {
            params.claim_date = claimDateByCodes;
        }
        dispatch(generateBulkTransfersCsvByPolicyCodesRequest(params));
    };

    // Afficher les résultats après génération
    useEffect(() => {
        if (generateBulkTransfersCsv.success && generateBulkTransfersCsv.data) {
            console.log('Fichiers générés (par date):', generateBulkTransfersCsv.data);
        }
    }, [generateBulkTransfersCsv.success, generateBulkTransfersCsv.data]);

    useEffect(() => {
        if (generateBulkTransfersCsvByPolicyCodes.success && generateBulkTransfersCsvByPolicyCodes.data) {
            console.log('Fichiers générés (par codes):', generateBulkTransfersCsvByPolicyCodes.data);
        }
    }, [generateBulkTransfersCsvByPolicyCodes.success, generateBulkTransfersCsvByPolicyCodes.data]);

    const handleGenerateEndOfStay = () => {
        const params = {};
        if (tripEndDateFrom) {
            params.trip_end_date_from = tripEndDateFrom;
        }
        if (tripEndDateTo) {
            params.trip_end_date_to = tripEndDateTo;
        }
        dispatch(generateBulkTransfersCsvEndOfStayRequest(params));
    };

    const handleGenerateEndOfStayByPolicyCodes = () => {
        const codes = policyCodesEndOfStay
            .split(',')
            .map(code => code.trim())
            .filter(code => code.length > 0);

        if (codes.length === 0) {
            alert('Veuillez entrer au moins un code de police');
            return;
        }

        const params = {
            policy_codes: codes,
        };
        dispatch(generateBulkTransfersCsvEndOfStayByPolicyCodesRequest(params));
    };

    return (
        <div className="dashboard-page admin-trips-bulk-transfers-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiDownload className="me-2" />
                                Trip - Génération de fichiers de virements en masse
                            </h2>
                            <p className="text-muted mb-0">
                                Génère deux fichiers : CSV pour les virements et Excel pour l'import paid daily
                            </p>
                        </div>
                        <div>
                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate(-1)}
                                className="d-flex align-items-center"
                            >
                                <FiArrowLeft className="me-2" />
                                Retour
                            </Button>
                        </div>
                    </div>

                    <Row className="mt-4">
                        <Col md={12}>
                            <Card className="mb-4">
                                <Card.Body>
                                    <Tab.Container activeKey={activeTab} onSelect={(k) => {
                                        setActiveTab(k);
                                        // Réinitialiser la méthode selon l'onglet sélectionné
                                        if (k === 'daily') {
                                            setMethod('byDate');
                                        } else if (k === 'endOfStay') {
                                            setMethod('endOfStay');
                                        }
                                    }}>
                                        <Nav variant="tabs" className="mb-3">
                                            <Nav.Item>
                                                <Nav.Link eventKey="daily">Daily</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="endOfStay">End of Stay</Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                        <Tab.Content>
                                            <Tab.Pane eventKey="daily">
                                                <Card>
                                                    <Card.Header>
                                                        <h5 className="mb-0">Méthode de génération - Daily</h5>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <Form.Group className="mb-3">
                                                            <Form.Check
                                                                type="radio"
                                                                label="Par date de claim"
                                                                name="method-daily"
                                                                value="byDate"
                                                                checked={method === 'byDate'}
                                                                onChange={(e) => setMethod(e.target.value)}
                                                                id="method-by-date"
                                                            />
                                                        </Form.Group>
                                                        <Form.Group className="mb-3">
                                                            <Form.Check
                                                                type="radio"
                                                                label="Par codes de police"
                                                                name="method-daily"
                                                                value="byPolicyCodes"
                                                                checked={method === 'byPolicyCodes'}
                                                                onChange={(e) => setMethod(e.target.value)}
                                                                id="method-by-codes"
                                                            />
                                                        </Form.Group>
                                                    </Card.Body>
                                                </Card>
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="endOfStay">
                                                <Card>
                                                    <Card.Header>
                                                        <h5 className="mb-0">Méthode de génération - End of Stay</h5>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <Form.Group className="mb-3">
                                                            <Form.Check
                                                                type="radio"
                                                                label="End of stay - Par date de fin de voyage"
                                                                name="method-end-of-stay"
                                                                value="endOfStay"
                                                                checked={method === 'endOfStay'}
                                                                onChange={(e) => setMethod(e.target.value)}
                                                                id="method-end-of-stay"
                                                            />
                                                        </Form.Group>
                                                        <Form.Group className="mb-3">
                                                            <Form.Check
                                                                type="radio"
                                                                label="End of stay - Par codes de police"
                                                                name="method-end-of-stay"
                                                                value="endOfStayByPolicyCodes"
                                                                checked={method === 'endOfStayByPolicyCodes'}
                                                                onChange={(e) => setMethod(e.target.value)}
                                                                id="method-end-of-stay-by-codes"
                                                            />
                                                        </Form.Group>
                                                    </Card.Body>
                                                </Card>
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Tab.Container>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {activeTab === 'daily' && method === 'byDate' && (
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <FiCalendar className="me-2" />
                                    Génération par date
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date de la claim (optionnel)</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={claimDate}
                                        onChange={(e) => setClaimDate(e.target.value)}
                                        placeholder="YYYY-MM-DD"
                                    />
                                    <Form.Text className="text-muted">
                                        Si non fournie, utilise la date du jour. Format: YYYY-MM-DD
                                    </Form.Text>
                                </Form.Group>

                                <Button
                                    variant="primary"
                                    onClick={handleGenerateByDate}
                                    disabled={generateBulkTransfersCsv.loading}
                                    className="mt-3"
                                >
                                    {generateBulkTransfersCsv.loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Génération en cours...
                                        </>
                                    ) : (
                                        <>
                                            <FiDownload className="me-2" />
                                            Générer les fichiers
                                        </>
                                    )}
                                </Button>

                                {generateBulkTransfersCsv.error && (
                                    <Alert variant="danger" className="mt-3">
                                        {generateBulkTransfersCsv.error}
                                    </Alert>
                                )}

                                {generateBulkTransfersCsv.success && generateBulkTransfersCsv.data && (
                                    <Alert variant="success" className="mt-3">
                                        <strong>Fichiers générés avec succès !</strong>
                                        <br />
                                        Les fichiers ont été uploadés dans le bucket S3 'claim-bulk-transfer' avec sous-dossier 'daily'.
                                        <FileDownloadSection data={generateBulkTransfersCsv.data} />
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {activeTab === 'daily' && method === 'byPolicyCodes' && (
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <FiFileText className="me-2" />
                                    Génération par codes de police
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Codes de police (requis)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={policyCodes}
                                        onChange={(e) => setPolicyCodes(e.target.value)}
                                        placeholder="Entrez les codes de police séparés par des virgules. Exemple: 658204658, 123456789"
                                    />
                                    <Form.Text className="text-muted">
                                        Séparez les codes par des virgules. Exemple: 658204658, 123456789
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Date de la claim (optionnel)</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={claimDateByCodes}
                                        onChange={(e) => setClaimDateByCodes(e.target.value)}
                                        placeholder="YYYY-MM-DD"
                                    />
                                    <Form.Text className="text-muted">
                                        Si non fournie, utilise la date du jour. Format: YYYY-MM-DD
                                    </Form.Text>
                                </Form.Group>

                                <Button
                                    variant="primary"
                                    onClick={handleGenerateByPolicyCodes}
                                    disabled={generateBulkTransfersCsvByPolicyCodes.loading}
                                    className="mt-3"
                                >
                                    {generateBulkTransfersCsvByPolicyCodes.loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Génération en cours...
                                        </>
                                    ) : (
                                        <>
                                            <FiDownload className="me-2" />
                                            Générer les fichiers
                                        </>
                                    )}
                                </Button>

                                {generateBulkTransfersCsvByPolicyCodes.error && (
                                    <Alert variant="danger" className="mt-3">
                                        {generateBulkTransfersCsvByPolicyCodes.error}
                                    </Alert>
                                )}

                                {generateBulkTransfersCsvByPolicyCodes.success && generateBulkTransfersCsvByPolicyCodes.data && (
                                    <Alert variant="success" className="mt-3">
                                        <strong>Fichiers générés avec succès !</strong>
                                        <br />
                                        Les fichiers ont été uploadés dans le bucket S3 'claim-bulk-transfer' avec sous-dossier 'daily'.
                                        <FileDownloadSection data={generateBulkTransfersCsvByPolicyCodes.data} />
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {activeTab === 'endOfStay' && method === 'endOfStay' && (
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <FiCalendar className="me-2" />
                                    Génération End of Stay - Par date de fin de voyage
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date de fin de voyage (from) - optionnel</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={tripEndDateFrom}
                                        onChange={(e) => setTripEndDateFrom(e.target.value)}
                                        placeholder="YYYY-MM-DD"
                                    />
                                    <Form.Text className="text-muted">
                                        Format: YYYY-MM-DD. Exemple: 2025-09-17
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Date de fin de voyage (to) - optionnel</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={tripEndDateTo}
                                        onChange={(e) => setTripEndDateTo(e.target.value)}
                                        placeholder="YYYY-MM-DD"
                                    />
                                    <Form.Text className="text-muted">
                                        Format: YYYY-MM-DD. Exemple: 2025-09-24
                                    </Form.Text>
                                </Form.Group>

                                <Button
                                    variant="primary"
                                    onClick={handleGenerateEndOfStay}
                                    disabled={generateBulkTransfersCsvEndOfStay.loading}
                                    className="mt-3"
                                >
                                    {generateBulkTransfersCsvEndOfStay.loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Génération en cours...
                                        </>
                                    ) : (
                                        <>
                                            <FiDownload className="me-2" />
                                            Générer les fichiers
                                        </>
                                    )}
                                </Button>

                                {generateBulkTransfersCsvEndOfStay.error && (
                                    <Alert variant="danger" className="mt-3">
                                        {generateBulkTransfersCsvEndOfStay.error}
                                    </Alert>
                                )}

                                {generateBulkTransfersCsvEndOfStay.success && generateBulkTransfersCsvEndOfStay.data && (
                                    <Alert variant="success" className="mt-3">
                                        <strong>Fichiers générés avec succès !</strong>
                                        <br />
                                        Les fichiers ont été uploadés dans le bucket S3 'claim-bulk-transfer' avec sous-dossier 'end_of_stay'.
                                        <FileDownloadSection data={generateBulkTransfersCsvEndOfStay.data} />
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {activeTab === 'endOfStay' && method === 'endOfStayByPolicyCodes' && (
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <FiFileText className="me-2" />
                                    Génération End of Stay - Par codes de police
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Codes de police (requis)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={policyCodesEndOfStay}
                                        onChange={(e) => setPolicyCodesEndOfStay(e.target.value)}
                                        placeholder="Entrez les codes de police séparés par des virgules. Exemple: 658204658, 123456789"
                                    />
                                    <Form.Text className="text-muted">
                                        Séparez les codes par des virgules. Exemple: 658204658, 123456789
                                    </Form.Text>
                                </Form.Group>

                                <Button
                                    variant="primary"
                                    onClick={handleGenerateEndOfStayByPolicyCodes}
                                    disabled={generateBulkTransfersCsvEndOfStayByPolicyCodes.loading}
                                    className="mt-3"
                                >
                                    {generateBulkTransfersCsvEndOfStayByPolicyCodes.loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Génération en cours...
                                        </>
                                    ) : (
                                        <>
                                            <FiDownload className="me-2" />
                                            Générer les fichiers
                                        </>
                                    )}
                                </Button>

                                {generateBulkTransfersCsvEndOfStayByPolicyCodes.error && (
                                    <Alert variant="danger" className="mt-3">
                                        {generateBulkTransfersCsvEndOfStayByPolicyCodes.error}
                                    </Alert>
                                )}

                                {generateBulkTransfersCsvEndOfStayByPolicyCodes.success && generateBulkTransfersCsvEndOfStayByPolicyCodes.data && (
                                    <Alert variant="success" className="mt-3">
                                        <strong>Fichiers générés avec succès !</strong>
                                        <br />
                                        Les fichiers ont été uploadés dans le bucket S3 'claim-bulk-transfer' avec sous-dossier 'end_of_stay'.
                                        <FileDownloadSection data={generateBulkTransfersCsvEndOfStayByPolicyCodes.data} />
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsBulkTransfers;
